/**
 * Global strip of INBOUND-REQUEST data from server-side Sentry events.
 *
 * Why this exists (GB #1561): the Node SDK's `RequestData` integration is on by
 * default and attaches the inbound request to every event captured while that
 * request is being handled. In @sentry/nextjs 7.120.4 its defaults are
 * `{ cookies: true, data: true, headers: true }`, and `sendDefaultPii` does NOT
 * gate those three — it only gates `ip` and `user`. So a capture whose own
 * payload is entirely value-free still ships:
 *
 *   - `request.data`     — the POSTed body. On checkout/user/order routes that
 *                          is the buyer's name, street, city, postal code,
 *                          phone, and payment-adjacent fields.
 *   - `request.cookies`  — `__session` / `auth`, a LIVE Firebase ID token. A
 *                          Sentry account compromise or over-broad team access
 *                          turns a stored event into account takeover.
 *   - `request.headers`  — the same token again in the `cookie` header, plus
 *                          `authorization` where a route uses a bearer token.
 *
 * This is invisible in any diff: no call site mentions request data. It was
 * caught only by ringing a real capture end-to-end during PR #1538's staging
 * ring, then counted across the codebase — 44 `Sentry.capture*` call sites in
 * 16 API route files, 13 of them checkout/user/order routes. (Counted with
 * `grep -rnE "Sentry\.capture(Exception|Message|Event)\(" src/pages/api`,
 * excluding tests — a looser grep reports 45 by also matching a prose mention
 * of `Sentry.capture*` in a comment.)
 *
 * The fix is ONE global hook rather than 45 patches, for the reason the scoped
 * fix in #1538 had to be widened twice: the leak is a property of the request
 * SCOPE, not of any call site, so it also covers captures inside dependencies
 * (Firestore services, vendor clients) and any capture added later by someone
 * who has never read this file.
 *
 * WHY `beforeSend` AND NOT A GLOBAL EVENT PROCESSOR: `beforeSend` is the last
 * hook in `_processEvent`, running after `prepareEvent` has already applied the
 * integration's processor that adds the request. A global event processor
 * registered at init could run BEFORE the integration's client processor, and
 * would then strip nothing. The real-SDK control case in
 * `src/utils/__tests__/sentryRequestPiiStrip.integration.test.ts` pins this: it
 * proves the harness sees the leak when the hook is absent.
 *
 * WHAT IS DELIBERATELY KEPT: `url`, `method`, and `query_string` — MINUS the
 * one-time credentials some query strings carry. They are the diagnosis (which
 * route, which verb), and the alert rules match on `transaction`, which is
 * derived from them, so blinding them would trade one failure for another.
 *
 * The exception is real and pre-dates this file. The hook's scope is every
 * server-side event, not only API routes, so it also covers SSR page errors —
 * and `/reset-password?oobCode=...` (plus `/action?mode=resetPassword&oobCode=...`,
 * src/pages/action.tsx) puts a LIVE password-reset credential in the page URL.
 * An SSR error on that page would persist a working account-takeover token into
 * Sentry. So the values of `oobCode`, `code`, and `token` are redacted from both
 * `url` and `query_string`; the parameter NAMES survive, which is the part that
 * aids diagnosis. API-route query strings were audited separately and carry only
 * ids, slugs, and provider enums.
 *
 * OUT OF SCOPE, said plainly:
 *
 * - The per-capture `extra`/`tags` payloads. A capture that puts a user value
 *   in `extra` is the GB #1550 family, audited clean today (50 sites), and not
 *   defended here.
 * - **Console-breadcrumb `message` and `data.arguments`.** Breadcrumb URL
 *   FIELDS (`from`/`to`/`url`) are redacted below, but the free text of a
 *   `console.*` breadcrumb is not scanned. Reachability from our own code is
 *   zero today: nothing on the credential-bearing pages (`action.tsx`,
 *   `reset-password.tsx`) logs to the console, so no `oobCode` can enter that
 *   way. The unmeasured residual is a THIRD-PARTY script logging a URL that
 *   happens to contain the token. Scanning arbitrary console text is a
 *   different, much broader problem than redacting known URL fields, so it is
 *   named here rather than half-solved.
 *
 * SCOPE: all three runtimes, but NOT the same treatment in each.
 *
 * The BODY/COOKIE/HEADER strip is server and edge only (`sentry.server.config.js`,
 * `sentry.edge.config.js`): the browser SDK's `request` carries no body and no
 * cookies, so there is nothing there to delete.
 *
 * The CREDENTIAL redaction runs everywhere, including the browser. Revision 2 of
 * this file claimed the client had "no privacy gain" to offer; that was WRONG,
 * and in the one place it matters most. `@sentry/browser`'s HttpContext
 * integration sets `request.url = window.location.href` and
 * `headers.Referer = document.referrer` on every event — and on
 * `/reset-password?oobCode=...` the page URL IS the credential. `action.tsx`
 * then `router.replace`s that URL, so the token also becomes the referrer of
 * every later navigation. A client-side error anywhere in that session would
 * have shipped a live password-reset token to Sentry.
 *
 * The EDGE wiring is defensive rather than corrective: `@sentry/vercel-edge`
 * registers its RequestData integration only when `sendDefaultPii` is set, and
 * the edge config does not set it, so edge events carry no request data today
 * and this hook strips nothing there. It is wired so that enabling
 * `sendDefaultPii` — which someone will do to get user context — cannot start
 * shipping the session cookie from middleware.
 */

import {
  sentryFloodGuardBeforeSend,
  type SentryEventLike,
} from './sentryFloodGuard';

/** Structural shape of the `request` on a Sentry event. Kept decoupled from the
 *  SDK types so this module is unit-testable without importing
 *  `@sentry/nextjs`, matching `sentryFloodGuard.ts`. */
export interface SentryRequestLike {
  url?: string;
  method?: string;
  query_string?: unknown;
  data?: unknown;
  cookies?: unknown;
  headers?: unknown;
  env?: unknown;
}

/** Structural shape of a breadcrumb whose `data` may carry URLs. */
export interface SentryBreadcrumbLike {
  data?: Record<string, unknown>;
}

/** Structural shape of the parts of a Sentry event this module touches. */
export interface SentryEventWithRequestLike {
  request?: SentryRequestLike;
  breadcrumbs?: SentryBreadcrumbLike[];
}

/**
 * Headers kept for diagnosis. This is an ALLOWLIST, not a denylist, because a
 * denylist is what rots: a header added by a future Next.js, Vercel, or auth
 * change is dropped by default here, whereas a denylist would ship it until
 * someone noticed. Nothing in this list is user-supplied identity:
 *
 *   user-agent               — browser/OS, the single most useful "only on X" clue
 *   content-type             — distinguishes a malformed body from a wrong encoding
 *   content-length           — distinguishes an empty body from a rejected one
 *   x-vercel-id              — ties the event to the Vercel log line for the request
 *   x-vercel-deployment-url  — which deployment served it (preview vs production)
 *
 * `referer` is deliberately absent: it is a full URL and query strings on other
 * origins can carry tokens.
 */
export const DIAGNOSTIC_HEADER_ALLOWLIST: readonly string[] = [
  'user-agent',
  'content-type',
  'content-length',
  'x-vercel-id',
  'x-vercel-deployment-url',
];

const ALLOWED_HEADERS = new Set(DIAGNOSTIC_HEADER_ALLOWLIST);

/**
 * Query parameters whose VALUE is a credential rather than a diagnostic.
 *
 * `oobCode` is Firebase's out-of-band action code — a live, single-use
 * password-reset / email-verification token (`/reset-password?oobCode=...`,
 * `/action?mode=resetPassword&oobCode=...`). `code` and `token` are the generic
 * spellings the same class arrives under from OAuth callbacks and magic links.
 *
 * Compared against the DECODED, lower-cased parameter name, and matched
 * exactly — never as a substring, so `error_code` and `stripe_error_code` keep
 * their values.
 */
const CREDENTIAL_QUERY_PARAMS = new Set([
  'oobcode',
  'code',
  'token',
  // `continueUrl` is Firebase's post-action redirect target, and it is an
  // ENCODED URL that routinely nests the action code inside itself — the live
  // format is `/action?mode=verifyEmail&oobCode=xxx&continueUrl=xxx`
  // (src/pages/action.tsx). Redacting only the outer `oobCode` would leave a
  // percent-encoded copy of it sitting in `continueUrl`. The whole value goes,
  // rather than recursing into it: a nested URL is not diagnosis worth the
  // parser.
  'continueurl',
  // GB #1561 follow-up, from Codex's post-merge P1 on 7d8f09ad2. These are the
  // CHECKOUT half of the same class, verified at their call sites:
  //
  //   confirm — base64 JSON of the "minimal order", built in
  //     checkout-v3/CheckoutProcessV3.tsx:320-356 and ExpressCheckoutButton.tsx.
  //     It carries buyer_email, and address {name, phone, country_code,
  //     state_province, city_locality, postal_code}, plus totals. It rides on
  //     BOTH `/checkout?order_id=..&confirm=..` (Affirm return) and
  //     `/order-confirmation/{id}?confirm=..`. Base64 is an encoding, not a
  //     protection — the value decodes to plain PII in one step.
  //
  //   payment_intent_client_secret — a LIVE Stripe payment credential, read
  //     from router.query at checkout.tsx:103 on the Stripe redirect return.
  //     Its format is `pi_xxx_secret_yyy`, so redacting it also removes the
  //     embedded payment-intent id.
  //
  //   redirect — a URL-valued param on the auth pages (auth/recover.tsx,
  //     verify.tsx). Same class as continueUrl: a nested, percent-encoded URL
  //     that can carry a credential the pair-level scan cannot see through.
  'confirm',
  'payment_intent_client_secret',
  'redirect',
  // GB #2075 (Codex re-review of PR #2093, HIGH): personal data rather than
  // credentials, redacted by the same rule. The browser SDK puts the page URL
  // (`window.location.href`) on every event, so a signup error logged on
  // `/signup?utm_campaign=Alice%20Smith&email=alice%40example.com&gclid=ABC`
  // shipped all three values even though the error message itself is
  // value-free. `utm_campaign` is free text a link author can fill with a
  // name or an email; `email` is an email; `gclid` is an ad click id. The
  // names survive, so a reader still sees the page came from a campaign.
  'utm_campaign',
  'email',
  'gclid',
  // GB #2075, fix list 4: free-text campaign fields and all ad click ids.
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_content',
  'fbclid',
  'msclkid',
  'gbraid',
  'wbraid',
]);

/**
 * DELIBERATELY NOT REDACTED, decided rather than overlooked — see the sweep
 * table in the PR body for all 17 page-level params.
 *
 * `payment_intent` (the bare `pi_xxx`): an identifier, not an authenticator.
 * Confirming or reading a PaymentIntent from the client requires the client
 * secret, which IS redacted above; server-side reads require our Stripe secret
 * key, which a Sentry reader does not thereby obtain. Against that, it is the
 * single highest-value join key in payment triage — it ties a Sentry event to
 * the exact Stripe payment and our order, which is the whole point of the
 * `stripe_error_type`/`stripe_error_code` tags. Redacting it would blind
 * exactly the failures this instrumentation exists to diagnose. Residual,
 * accepted: it identifies one buyer's transaction to anyone with Sentry access.
 *
 * `id` / `order_id`: same reasoning, and redacting them would also be theatre —
 * both appear in the URL PATH on their pages (`/order-confirmation/{id}`),
 * which is kept by design, so filtering the query copy alone would protect
 * nothing while blinding order triage.
 *
 * `search` (help-centre query): user-typed free text, so it CAN contain PII if
 * a user types it, but it carries none by construction and it is real
 * diagnosis. Named as a residual instead of redacted.
 */

const REDACTED = '[Filtered]';

function isCredentialParam(rawKey: string): boolean {
  let key = rawKey;
  try {
    key = decodeURIComponent(rawKey);
  } catch {
    // Malformed percent-encoding: fall back to the raw key rather than throw.
    // A throw here would trip the fail-closed catch and drop the event.
  }
  return CREDENTIAL_QUERY_PARAMS.has(key.trim().toLowerCase());
}

/**
 * Replace credential VALUES in a parameter string; parameter names survive.
 *
 * Rewrites pair-by-pair with a regex rather than `split('&').join('&')` so the
 * ORIGINAL SEPARATORS are preserved. That matters because `;` is a legacy but
 * still-accepted parameter separator: `?a=1;oobCode=SECRET` is one pair to a
 * naive `&` split — key `a`, value `1;oobCode=SECRET` — and the credential
 * rides through inside the value. Splitting on both and rejoining would have to
 * guess which separator to write back; matching pairs in place does not.
 */
function redactQueryString(query: string): string {
  return query.replace(/([^&;=]+)=([^&;]*)/g, (pair, key: string) =>
    isCredentialParam(key) ? `${key}=${REDACTED}` : pair
  );
}

/**
 * Same redaction applied to a URL, INCLUDING the hash fragment.
 *
 * Deliberately string-based rather than `new URL()`: the SDK often reports a
 * path (`/reset-password?oobCode=...`) rather than an absolute URL, and
 * `new URL()` throws on those.
 *
 * The fragment is not an edge case here. On the client, `request.url` is
 * `window.location.href`, which includes the fragment verbatim, and a
 * credential can arrive as `#oobCode=SECRET` with no query string at all — or
 * as `/r?a=1#oobCode=SECRET`, where treating everything after the first `?` as
 * one query string parses the whole fragment as the VALUE of `a` and passes it
 * straight through. Both shapes leaked before this split existed.
 *
 * Per RFC 3986 the fragment runs to the end of the URL, so the first `#` is the
 * boundary and any `?` after it belongs to the fragment. A plain anchor
 * (`#section`) has no `=` and is left alone.
 */
export function redactUrl(url: string): string {
  const hash = url.indexOf('#');
  if (hash === -1) return redactUrlPath(url);

  const path = redactUrlPath(url.slice(0, hash));
  const fragment = redactQueryString(url.slice(hash + 1));
  return `${path}#${fragment}`;
}

/** The `?`-query half of a URL, fragment already removed. */
function redactUrlPath(url: string): string {
  const q = url.indexOf('?');
  if (q === -1) return url;
  return `${url.slice(0, q + 1)}${redactQueryString(url.slice(q + 1))}`;
}

/**
 * `query_string` is loosely typed in the SDK: a raw string, an array of
 * `[key, value]` pairs, or a plain record, depending on how the event was
 * built. All three shapes are handled; anything else is left alone.
 */
function redactQueryStringField(value: unknown): unknown {
  if (typeof value === 'string') return redactQueryString(value);

  if (Array.isArray(value)) {
    return value.map((pair) =>
      Array.isArray(pair) && pair.length === 2
        ? [pair[0], redactPairValue(String(pair[0]), pair[1])]
        : pair
    );
  }

  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      out[key] = redactPairValue(key, v);
    }
    return out;
  }

  return value;
}

/**
 * Redact one already-parsed `key=value` pair.
 *
 * The value is scanned too, not just the key — and that is the whole point of
 * this function. A PRODUCTION RING CAUGHT THIS: Sentry delivers `query_string`
 * as pairs already split on `&` ONLY, so `?a=1;oobCode=SECRET` arrives as key
 * `a` with value `1;oobCode=SECRET`, and a key-only check passes it straight
 * through. The unit test for the `;` separator passed the whole time because it
 * fed the STRING form, which this path never produces — the test shared the
 * author's assumption about the shape, exactly the blind spot that made #1561
 * necessary in the first place.
 *
 * Running the same pair-level redaction over the value handles any separator
 * the upstream split missed. It cannot over-redact: only credential-named keys
 * inside the value are touched, so `1;error_code=x` comes back unchanged.
 */
function redactPairValue(key: string, value: unknown): unknown {
  if (isCredentialParam(key)) return REDACTED;
  return typeof value === 'string' ? redactQueryString(value) : value;
}

/** Breadcrumb `data` keys that hold a URL. Navigation breadcrumbs use
 *  `from`/`to`; fetch and xhr breadcrumbs use `url`. */
const BREADCRUMB_URL_KEYS = ['from', 'to', 'url'];

/**
 * Redact one-time credentials from every URL-shaped field on an event.
 *
 * Runs on ALL runtimes. The browser is the reason it exists: HttpContext sets
 * `request.url` from `window.location.href` and `Referer` from
 * `document.referrer`, and the Breadcrumbs integration records navigation
 * `from`/`to` — so on `/reset-password?oobCode=...` the credential reaches
 * Sentry through three separate channels, none of which is a request body.
 *
 * REFERER IS REDACTED, NOT DROPPED. Dropping it would lose real diagnosis (the
 * page a user came from) and the extra safety is close to nil: with no
 * `Referrer-Policy` header set in `next.config.js` — checked, there is none —
 * browsers apply the modern default `strict-origin-when-cross-origin`, under
 * which a CROSS-ORIGIN referrer arrives as a bare origin with no path and no
 * query. The only referrers that can carry a query string are same-origin,
 * i.e. our own URLs, which this redaction covers by construction. Dropping
 * would therefore cost diagnosis to defend against a case the browser has
 * already prevented.
 *
 * Every field is rebuilt rather than mutated in place, for the same aliasing
 * reason documented on the header strip below.
 */
export function redactEventCredentials<T extends SentryEventWithRequestLike>(
  event: T
): T {
  const request = event?.request;

  if (request) {
    if (typeof request.url === 'string') {
      request.url = redactUrl(request.url);
    }
    if (request.query_string !== undefined) {
      request.query_string = redactQueryStringField(request.query_string);
    }

    const { headers } = request;
    if (headers && typeof headers === 'object' && !isIterableHeaders(headers)) {
      // BUILD A NEW OBJECT — do not write into `headers`. This is the line
      // that owns the aliasing guarantee for the whole module, because it runs
      // FIRST on both runtimes. `extractRequestData` (node) assigns
      // `event.request.headers` by REFERENCE to the live `req.headers`, so
      // redacting a Referer in place would rewrite a header on the real
      // inbound request mid-flight. Everything downstream of here — including
      // the allowlist in `stripRequestPii` — is already working on this copy.
      // Pinned by a test that asserts the caller's object is unchanged.
      const next: Record<string, unknown> = {};
      for (const [name, value] of Object.entries(
        headers as Record<string, unknown>
      )) {
        const isReferer = /^referr?er$/i.test(name);
        next[name] =
          isReferer && typeof value === 'string' ? redactUrl(value) : value;
      }
      request.headers = next;
    }
  }

  if (Array.isArray(event?.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      const data = crumb?.data;
      if (!data || typeof data !== 'object') return crumb;

      let changed = false;
      const nextData: Record<string, unknown> = { ...data };
      for (const key of BREADCRUMB_URL_KEYS) {
        const value = nextData[key];
        if (typeof value === 'string') {
          const redacted = redactUrl(value);
          if (redacted !== value) {
            nextData[key] = redacted;
            changed = true;
          }
        }
      }
      return changed ? { ...crumb, data: nextData } : crumb;
    });
  }

  return event;
}

/**
 * `Headers` and `Map` instances have no own enumerable properties, so
 * `Object.entries` returns `[]` on them. Detect that shape and leave it alone
 * rather than silently replacing every header with an empty object.
 *
 * ⚠ NEVER WIDEN THIS CONDITION. It FAILS OPEN: for a matching shape the strip
 * is skipped entirely, so cookie and authorization headers would pass through
 * untouched. It is safe today only because it is unreachable — two independent
 * reasons, both checked rather than assumed:
 *
 *   1. Neither the Node `RequestData` integration nor the browser
 *      `HttpContext` integration produces a `Headers`/`Map`; both build a plain
 *      object.
 *   2. Even if one did, `prepareEvent`'s `normalize()` (@sentry/core
 *      prepareEvent.js:109-110) flattens the event before `beforeSend` runs, so
 *      a `Headers` instance would already have been reduced to `{}` by the time
 *      this sees it.
 *
 * So this branch trades a silent total header loss for a fail-open path that
 * nothing can currently reach. Loosening the test — say, dropping the
 * `Object.keys().length === 0` half — would make it reachable by a plain object
 * that happens to have a `forEach` property, and that is a real leak. If a
 * future SDK genuinely passes iterable headers, ITERATE THEM AND APPLY THE
 * ALLOWLIST; do not extend the skip.
 */
function isIterableHeaders(headers: object): boolean {
  return (
    typeof (headers as { forEach?: unknown }).forEach === 'function' &&
    Object.keys(headers).length === 0
  );
}

/**
 * Remove the request body, cookies, environment, and every non-allowlisted
 * header from an event. Mutates and returns the same event, which is what
 * `beforeSend` expects.
 *
 * `env` is dropped as well: it is not in the integration's defaults today, but
 * when populated it carries `REMOTE_ADDR` (the caller's IP).
 */
export function stripRequestPii<T extends SentryEventWithRequestLike>(
  event: T
): T {
  const request = event?.request;
  if (!request) return event;

  delete request.data;
  delete request.cookies;
  delete request.env;

  // Same credential redaction the client runs. On the server the header
  // allowlist below already drops `referer`, so only `url` and `query_string`
  // are load-bearing here — but sharing one function is what keeps the two
  // runtimes from drifting apart.
  redactEventCredentials(event);

  const { headers } = request;
  if (headers && typeof headers === 'object' && !isIterableHeaders(headers)) {
    // Also builds a new object rather than deleting keys in place. Note this
    // is now belt-and-braces rather than the primary defence: by the time this
    // runs, `redactEventCredentials` above has already replaced
    // `request.headers` with a copy, so the live `req.headers` is out of reach
    // here. Mutation testing caught exactly that — an in-place delete on this
    // line no longer breaks the aliasing test, because the copy happens
    // upstream. The rule still holds for whoever edits this next: never write
    // into an object the SDK handed us by reference.
    const kept: Record<string, unknown> = {};
    for (const [name, value] of Object.entries(
      headers as Record<string, unknown>
    )) {
      if (ALLOWED_HEADERS.has(name.toLowerCase())) kept[name] = value;
    }
    request.headers = kept;
  }

  return event;
}

/**
 * Wrap an existing `beforeSend` so the strip runs FIRST, then delegate.
 *
 * Order matters in one direction only: running the strip first means the PII is
 * gone before any other hook can read, copy, fingerprint, or log it. The
 * downstream hook (today the flood guard) still decides whether the event is
 * sent at all.
 *
 * FAILS CLOSED, deliberately, and unlike the flood guard it wraps. The flood
 * guard fails open because the worst case of a guard bug is a lost error. The
 * worst case of a strip bug is transmitting a live session token to a third
 * party, so if this throws the event is dropped instead of sent unstripped.
 * `delete` on three optional properties plus an `Object.entries` walk can
 * realistically only throw on a frozen or throwing-getter event object, which
 * is not a shape the SDK produces — but "not today's shape" is not a guarantee
 * worth a credential.
 */
export function withRequestPiiStrip<
  T extends SentryEventWithRequestLike,
  H = unknown,
>(next?: (event: T, hint?: H) => T | null): (event: T, hint?: H) => T | null {
  return withEventGuard(stripRequestPii, next);
}

/**
 * Same guard, credential redaction only — no body/cookie/header removal.
 *
 * This is the CLIENT hook. Browser events have no body and no cookies to
 * remove, and their headers (`Referer`, `User-Agent`) are diagnosis worth
 * keeping, so the server's allowlist would be pure loss there. What the browser
 * does have is the credential in `window.location.href`, `document.referrer`,
 * and navigation breadcrumbs — which is exactly what this redacts.
 */
export function withCredentialRedaction<
  T extends SentryEventWithRequestLike,
  H = unknown,
>(next?: (event: T, hint?: H) => T | null): (event: T, hint?: H) => T | null {
  return withEventGuard(redactEventCredentials, next);
}

function withEventGuard<T extends SentryEventWithRequestLike, H = unknown>(
  transform: (event: T) => T,
  next?: (event: T, hint?: H) => T | null
): (event: T, hint?: H) => T | null {
  return (event: T, hint?: H): T | null => {
    let stripped: T;
    try {
      stripped = transform(event);
    } catch (err) {
      // Say so. A silent fail-closed drop is the #1466 "quiet Sentry" shape:
      // events stop arriving and nothing indicates why. This lands in the
      // Vercel function log, and the Console integration records `console.*`
      // as a BREADCRUMB rather than an event, so there is no recursion back
      // into beforeSend.
      // eslint-disable-next-line no-console
      console.error('[sentry] request PII strip failed; event dropped', err);
      return null;
    }
    return next ? next(stripped, hint) : stripped;
  };
}

/**
 * The `beforeSend` used by BOTH server-side runtimes (`sentry.server.config.js`
 * and `sentry.edge.config.js`).
 *
 * Composed here rather than in the config files so the composition is written
 * once and type-checked — the configs are `.js` and would type-check nothing.
 * Strip first, flood guard second: the guard reads the message and tags to build
 * its fingerprint, and it should never see a body it might carry into one.
 */
export const sentryServerBeforeSend = withRequestPiiStrip<
  SentryEventLike & SentryEventWithRequestLike
>(sentryFloodGuardBeforeSend);

/**
 * The same strip for TRANSACTION events, which `beforeSend` never sees —
 * `processBeforeSend` routes them to `beforeSendTransaction` instead.
 *
 * Nothing produces transactions today (the Node config sets no
 * `tracesSampleRate`; the edge config sets it to 0), so this is inert right now.
 * It is wired anyway because a transaction carries the same `request` object,
 * which means turning tracing on — a one-line change someone will make for
 * performance work, with no reason to think about PII — would silently reopen
 * exactly this leak on every sampled checkout request.
 *
 * WITHOUT the flood guard, deliberately. The guard fingerprints by exception or
 * message; transactions have neither, so every transaction would collapse into
 * one fingerprint and all but 5 per 10s would be dropped. Rate limiting
 * transactions is what `tracesSampleRate` is for.
 */
export const sentryServerBeforeSendTransaction = withRequestPiiStrip<
  SentryEventWithRequestLike
>();

/**
 * The `beforeSend` used by the BROWSER (`sentry.client.config.js`).
 *
 * Credential redaction plus the existing flood guard, in that order. It does
 * NOT run the body/cookie/header strip — see `withCredentialRedaction` for why
 * that would be pure diagnosis loss on the client.
 *
 * Note for anyone reading the client config: `ignoreErrors` and `denyUrls`
 * filter events BEFORE `beforeSend` runs, so an ignored error never reaches
 * this hook. That is fine — an event that is never sent cannot leak — but it
 * does mean this hook is not a place to count what the browser produced.
 */
export const sentryClientBeforeSend = withCredentialRedaction<
  SentryEventLike & SentryEventWithRequestLike
>(sentryFloodGuardBeforeSend);
